import type { FieldValues } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { Box, InputAdornment, TextField, Typography } from '@mui/material';
import type { FieldBaseProps } from './fields/types';

export interface IncludedValueFieldProps<T extends FieldValues> extends FieldBaseProps<T> {
  /** Trailing unit adornment — e.g. "GB", "min", "msgs". */
  unit: string;
  /** Numeric step (GB uses 0.1; counts use 1). */
  step?: string;
  placeholder?: string;
  /**
   * When true (its matching "Unlimited" toggle is on), the input greys out and
   * shows a muted "—" instead of an editable field — the allowance is
   * unbounded, so a value would be meaningless.
   */
  gated?: boolean;
  /** Helper text shown while gated (translated copy). */
  gatedHelperText?: string;
}

/**
 * The gated included-allowance input for a plan dimension. Editable numeric
 * field with a unit adornment; when `gated` (Unlimited on) it collapses to a
 * greyed, non-editable "—" so the UI makes the "no meaningful value" state
 * explicit. Pairs with {@link DimensionBlock}. The form clears the underlying
 * value when it flips Unlimited on, so the round-trip payload stays correct.
 */
export function IncludedValueField<T extends FieldValues>({
  control,
  name,
  label,
  helperText,
  required,
  unit,
  step = '1',
  placeholder,
  gated = false,
  gatedHelperText,
}: IncludedValueFieldProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        if (gated) {
          return (
            <Box>
              <Typography
                component="label"
                sx={{ display: 'block', fontSize: 13, color: 'text.secondary', mb: 0.5 }}
              >
                {label}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: 40,
                  px: '12px',
                  borderRadius: '8px',
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.default',
                  color: 'text.secondary',
                  opacity: 0.7,
                }}
              >
                <Typography component="span" sx={{ fontSize: 14, fontWeight: 500 }}>
                  —
                </Typography>
              </Box>
              {gatedHelperText && (
                <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
                  {gatedHelperText}
                </Typography>
              )}
            </Box>
          );
        }
        return (
          <TextField
            {...field}
            size="small"
            value={field.value ?? ''}
            label={label}
            placeholder={placeholder}
            type="number"
            required={required}
            fullWidth
            error={!!fieldState.error}
            helperText={fieldState.error?.message ?? helperText}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">{unit}</InputAdornment>,
              },
              htmlInput: { step, min: 0, inputMode: 'decimal' },
            }}
          />
        );
      }}
    />
  );
}
